/**
 * Combined Rashi · Graha · Bhava tatva (pancha-mahabhuta) reference for chart reading.
 * Bhava cycle: अग्नि → पृथ्वी → वायु → जल from 1st house (fixed for every chart).
 */
import { RASHI_CHART_ROW_ENGLISH, RASHI_CHART_ROWS } from './jyotishaRashiChart';

/** @typedef {'agni' | 'prithvi' | 'vayu' | 'jal' | 'akasha'} TatvaId */

export const TATVA_META = {
  agni: {
    id: 'agni',
    hi: 'अग्नि',
    en: 'Fire',
    cycleOrder: 0,
  },
  prithvi: {
    id: 'prithvi',
    hi: 'पृथ्वी',
    en: 'Earth',
    cycleOrder: 1,
  },
  vayu: {
    id: 'vayu',
    hi: 'वायु',
    en: 'Air',
    cycleOrder: 2,
  },
  jal: {
    id: 'jal',
    hi: 'जल',
    en: 'Water',
    cycleOrder: 3,
  },
  akasha: {
    id: 'akasha',
    hi: 'आकाश',
    en: 'Ether / Space',
    cycleOrder: 4,
  },
};

export { TATVA_IMAGE_BY_ID, normalizeTatvaId, tatvaImageSrc } from './jyotishaTatvaImages';

const CYCLE_IDS = /** @type {TatvaId[]} */ (['agni', 'prithvi', 'vayu', 'jal']);

/** Hindi tatva label → canonical id */
const HI_TO_ID = {
  अग्नि: 'agni',
  पृथ्वी: 'prithvi',
  वायु: 'vayu',
  जल: 'jal',
  आकाश: 'akasha',
};

/**
 * @param {string} hiLabel
 * @returns {TatvaId | null}
 */
export function tatvaIdFromHi(hiLabel) {
  return HI_TO_ID[hiLabel] ?? null;
}

/**
 * @param {TatvaId | null} id
 */
export function tatvaMetaFromId(id) {
  return id ? TATVA_META[id] : null;
}

/** @param {number} house 1-12 */
export function bhavaTatvaIdForHouse(house) {
  return CYCLE_IDS[(house - 1) % 4];
}

export const RASHI_TATVA_ROWS = RASHI_CHART_ROWS.map((row, i) => {
  const id = tatvaIdFromHi(row.tatva);
  const meta = tatvaMetaFromId(id);
  return {
    num: row.num,
    rashiHi: row.rashi,
    rashiEn: RASHI_CHART_ROW_ENGLISH[i],
    tatvaHi: row.tatva,
    tatvaEn: meta?.en ?? row.tatva,
    tatvaId: id,
  };
});

/** Navagraha natural tatva (Rahu/Ketu: no fixed tatva in this chart-reading sheet). */
export const GRAHA_TATVA_ROWS = [
  { planetEn: 'Sun', planetHi: 'सूर्य', tatvaId: 'agni', tatvaHi: 'अग्नि', tatvaEn: 'Fire' },
  { planetEn: 'Moon', planetHi: 'चंद्र', tatvaId: 'jal', tatvaHi: 'जल', tatvaEn: 'Water' },
  { planetEn: 'Mars', planetHi: 'मंगल', tatvaId: 'agni', tatvaHi: 'अग्नि', tatvaEn: 'Fire' },
  { planetEn: 'Mercury', planetHi: 'बुध', tatvaId: 'prithvi', tatvaHi: 'पृथ्वी', tatvaEn: 'Earth' },
  { planetEn: 'Jupiter', planetHi: 'गुरु', tatvaId: 'akasha', tatvaHi: 'आकाश', tatvaEn: 'Ether / Space' },
  { planetEn: 'Venus', planetHi: 'शुक्र', tatvaId: 'jal', tatvaHi: 'जल', tatvaEn: 'Water' },
  { planetEn: 'Saturn', planetHi: 'शनि', tatvaId: 'vayu', tatvaHi: 'वायु', tatvaEn: 'Air' },
  { planetEn: 'Rahu', planetHi: 'राहु', tatvaId: null, tatvaHi: null, tatvaEn: null, noTatva: true },
  { planetEn: 'Ketu', planetHi: 'केतु', tatvaId: null, tatvaHi: null, tatvaEn: null, noTatva: true },
];

export const BHAVA_TATVA_ROWS = Array.from({ length: 12 }, (_, i) => {
  const house = i + 1;
  const tatvaId = bhavaTatvaIdForHouse(house);
  const meta = TATVA_META[tatvaId];
  return {
    house,
    tatvaId,
    tatvaHi: meta.hi,
    tatvaEn: meta.en,
  };
});

/** Chart polygon fills by bhava tatva (agni → prithvi → vayu → jal cycle from lagna). */
export const BHAVA_TATVA_CHART_FILL = {
  agni: { dark: 'rgba(248, 113, 113, 0.4)', light: 'rgba(254, 202, 202, 0.78)' },
  prithvi: { dark: 'rgba(52, 211, 153, 0.34)', light: 'rgba(167, 243, 208, 0.78)' },
  vayu: { dark: 'rgba(203, 213, 225, 0.48)', light: 'rgba(226, 232, 240, 0.92)' },
  jal: { dark: 'rgba(56, 189, 248, 0.38)', light: 'rgba(186, 230, 253, 0.82)' },
};

/**
 * Per-house `houseFill` for NorthIndianChart via `metadataByHouse`.
 * @param {boolean} isDark
 */
export function createBhavaTatvaMetadataByHouse(isDark) {
  return Object.fromEntries(
    BHAVA_TATVA_ROWS.map((row) => {
      const fills = BHAVA_TATVA_CHART_FILL[row.tatvaId];
      return [row.house, { houseFill: isDark ? fills.dark : fills.light }];
    })
  );
}
